import appEnvironmentReducer from '@/app/store/appEnvironmentSlice';
import { configureStore } from '@reduxjs/toolkit';
import type { Middleware, Reducer } from '@reduxjs/toolkit';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { TypedUseSelectorHook } from 'react-redux';
import { menuApiRedux } from '@/shared/features/menu/infra/adapters/menuApiSlice';
import { searchApiRedux } from '@/features/search/infra/adapters/search.adapter';
import { homePageApiRedux } from '@/features/home/infra/adapters/home.adapter';
import { userContextApiRedux } from '@/shared/features/user-context/infra/adapters/userContextApiSlice';
import { plpApiRedux } from '@/features/plp/infra/adapters/plp.adapter';
import { bannerWidgetApiRedux } from '@/shared/features/banner-widget/infra/adapters/bannerWidget.adapter';
import { physicalStoreApiRedux } from '@/shared/features/physical-store/infra/adapters/physicalStore.adapter';
import { wishlistApiRedux } from '@/features/wishlist/infra/adapters/wishlist.adapter';
import { browsingHistoryApiRedux } from '@/features/browsing-history/infra/adapters/browsingHistory.adapter';
import { videoCallApiRedux } from '@/features/video-call/infra/adapters/videoCall.adapter';
import { plpInitialReducer, PLP_INITIAL_SLICE_NAME } from '@/features/plp/presentation/store/plpInitialSlice';
import { loginUiReducer } from '@/app/store/loginUiSlice';
import { searchUiReducer } from '@/app/store/searchUiSlice';
import { headerConfigReducer } from '@/app/store/headerConfigSlice';
import { footerVisibilityReducer } from '@/app/store/footerVisibilitySlice';
import { newHomeUiReducer } from '@/app/store/newHomeUiSlice';
import { videoCallReducer, VIDEO_CALL_SLICE_NAME } from '@/features/video-call/presentation/store/videoCallSlice';
import type { MenuApiResponse } from '@/shared/features/menu/infra/adapters/menuApiSlice';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';
import { abTestingReducer } from '@/shared/features/ab-testing/store/abTestingSlice';
import { bsUtilApiRedux } from '@/features/bs-util/infra/adapters/bsUtilApiSlice';
import { featureFlagsApiRedux } from '@/features/feature-flags/infra/adapters/featureFlagsApiSlice';
import { posApprovalApiRedux } from '@/features/pos-approval/infra/adaptors/pos_approval.adaptor';
import { PosApprovalModel } from '@/features/pos-approval/domain/models/PosApprovalModel';
import { pdpApiRedux } from '@/features/pdp/infra/adapters/pdp.adapter';
import { customizationApiRedux as pdpCustomizationApiRedux } from '@/features/customization/infra/adapters/customization.adapter';
import { sizerApiRedux } from '@/features/sizer/infra/adapters/sizer.adapter';
import { pdpDeliveryApiRedux } from '@/features/pdp/infra/adapters/pdpDelivery.adapter';
import { pdpStoreApiRedux } from '@/features/pdp/infra/adapters/pdpStore.adapter';
import { similarDesignsApiRedux } from '@/features/similar-designs/infra/adapters/similarDesigns.adapter';
import { matchingSetApiRedux } from '@/features/matching-sets/infra/adapters/matchingSet.adapter';
import { posCartApiRedux } from '@/features/posCart/infra/adapters/posCart.adapter';
import { pincodeApiRedux } from '@/shared/features/pincode/infra/adapters/pincode.adapter';
import pincodeReducer, { PINCODE_SLICE_NAME } from '@/shared/features/pincode/presentation/store/pincodeSlice';
import { pdpInitialReducer, PDP_INITIAL_SLICE_NAME } from '@/app/store/pdpInitialSlice';
import {
  storeGiftCardInitialReducer,
  STORE_GIFT_CARD_INITIAL_SLICE_NAME,
} from '@/features/store-gc/presentation/store/storeGiftCardInitialSlice';
import {
  storeLocatorInitialReducer,
  STORE_LOCATOR_INITIAL_SLICE_NAME,
} from '@/shared/features/physical-store/presentation/store/storeLocatorInitialSlice';
import type { StoreLocatorData } from '@/shared/features/physical-store/domain/models/StoreLocatorData';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import type { PhysicalGiftCardConfig } from '@/features/store-gc/domain/models/storeGiftCard.types';
import { paymentApiRedux } from '@/features/payment/infra/adapters/payment.adapter';
import { multivariateExperimentReducer, MULTIVARIATE_EXPERIMENT_SLICE_NAME } from '@/shared/features/multivariateexperiment/store/multivariateExperimentSlice';
import { parseExperimentCookies } from '@/shared/features/multivariateexperiment/utils/multivariateExperimentUtils';

/**
 * Redux Store Configuration
 * Uses query client (currently RTK Query) for API state management
 * Combines feature-specific query API slices
 */

/**
 * Create preloaded state for query client with menu data and home page data
 * Used for both SSR and client-side hydration.
 * menuResult is the full getMenu() response (hamburger + desktop) from the single SSR API call.
 */
export function createPreloadedState(
  _menuResult: MenuApiResponse | null,
  _homePageData: Record<string, unknown> | null = null,
  userContextData: Record<string, unknown> | null = null,
  abUid: string | null = null,
  initialPlpData: ListingPageResult | null = null,
  initialPlpUrl: string | null = null,
  initialPosApprovalData: PosApprovalModel | null = null,
  initialPdpData: ProductDetail | null = null,
  initialStoreGiftCardConfig: PhysicalGiftCardConfig | null = null,
  initialStoreLocatorData: StoreLocatorData | null = null
): Record<string, unknown> | undefined {
  const hasUserContextData = userContextData !== null;
  const hasAbUid = Boolean(abUid);
  const hasPlpData = initialPlpData !== null && initialPlpData !== undefined;
  const hasPosApprovalData = initialPosApprovalData !== null;
  const hasPdpData = initialPdpData !== null && initialPdpData !== undefined;
  const hasStoreGiftCardConfig =
    initialStoreGiftCardConfig !== null && initialStoreGiftCardConfig !== undefined;
  const hasStoreLocatorData =
    initialStoreLocatorData !== null && initialStoreLocatorData !== undefined;

  // Note: Menu and home page data are initialized via RTK Query's upsertQueryData
  // in ReduxProvider to avoid updateProvidedBy hydration errors
  // Only include non-RTK Query state in preloadedState

  if (
    !hasUserContextData &&
    !hasAbUid &&
    !hasPlpData &&
    !hasPosApprovalData &&
    !hasPdpData &&
    !hasStoreGiftCardConfig &&
    !hasStoreLocatorData &&
    typeof window === 'undefined'
  ) {
    return undefined;
  }

  const preloadedState: Record<string, unknown> = {};

  if (hasUserContextData) {
    preloadedState.userContextApi = {
      queries: {
        'getUserContext(undefined)': {
          status: 'fulfilled' as const,
          data: userContextData,
          fulfilledTimeStamp: Date.now(),
        },
      },
      mutations: {},
      provided: {},
      subscriptions: {},
      config: {
        reducerPath: 'userContextApi',
        keepUnusedDataFor: 60,
      },
    };
  }

  if (hasAbUid) {
    preloadedState.abTesting = {
      abUid: abUid as string,
    };
  }

  if (hasPlpData) {
    preloadedState[PLP_INITIAL_SLICE_NAME] = {
      data: initialPlpData,
      url: initialPlpUrl,
    };
  }

  if (typeof window !== 'undefined') {
    const parsedClientExperiments = parseExperimentCookies(document.cookie);
    preloadedState[MULTIVARIATE_EXPERIMENT_SLICE_NAME] = parsedClientExperiments;
  }

  if (hasPosApprovalData) {
    preloadedState.posApprovalData = {
      data: initialPosApprovalData,
    };
  }

  if (hasPdpData) {
    preloadedState[PDP_INITIAL_SLICE_NAME] = {
      data: initialPdpData,
    };
  }

  if (hasStoreGiftCardConfig) {
    preloadedState[STORE_GIFT_CARD_INITIAL_SLICE_NAME] = {
      data: initialStoreGiftCardConfig,
    };
  }

  if (hasStoreLocatorData) {
    preloadedState[STORE_LOCATOR_INITIAL_SLICE_NAME] = {
      data: initialStoreLocatorData,
    };
  }

  return preloadedState;
}

/**
 * Create Redux Store
 * Factory function to create store with optional preloaded state
 * Used by both server-side (SSR) and client-side
 * Combines multiple feature-specific query API slices
 * 
 * Note: When using non-Redux adapters (like Fetch), reducers and middleware
 * are not added to the store. The store is still needed for other application state.
 */
export function createStore(preloadedState?: Record<string, unknown>) {
  // Check if Redux properties exist (RTK Query adapter)
  // Non-Redux adapters (like Fetch) won't have these properties
  const hasReduxSupport =
    'reducerPath' in menuApiRedux &&
    'reducer' in menuApiRedux &&
    'middleware' in menuApiRedux &&
    'reducerPath' in searchApiRedux &&
    'reducer' in searchApiRedux &&
    'middleware' in searchApiRedux &&
    'reducerPath' in homePageApiRedux &&
    'reducer' in homePageApiRedux &&
    'middleware' in homePageApiRedux &&
    'reducerPath' in userContextApiRedux &&
    'reducer' in userContextApiRedux &&
    'middleware' in userContextApiRedux &&
    'reducerPath' in plpApiRedux &&
    'reducer' in plpApiRedux &&
    'middleware' in plpApiRedux &&
    'reducerPath' in bannerWidgetApiRedux &&
    'reducer' in bannerWidgetApiRedux &&
    'middleware' in bannerWidgetApiRedux &&
    'reducerPath' in physicalStoreApiRedux &&
    'reducer' in physicalStoreApiRedux &&
    'middleware' in physicalStoreApiRedux &&
    'reducerPath' in wishlistApiRedux &&
    'reducer' in wishlistApiRedux &&
    'middleware' in wishlistApiRedux &&
    'reducerPath' in browsingHistoryApiRedux &&
    'reducer' in browsingHistoryApiRedux &&
    'middleware' in browsingHistoryApiRedux &&
    'reducerPath' in videoCallApiRedux &&
    'reducer' in videoCallApiRedux &&
    'middleware' in videoCallApiRedux &&
    'reducerPath' in bsUtilApiRedux &&
    'reducer' in bsUtilApiRedux &&
    'middleware' in bsUtilApiRedux &&
    'reducerPath' in posApprovalApiRedux &&
    'reducer' in posApprovalApiRedux &&
    'middleware' in posApprovalApiRedux &&
    'reducerPath' in pdpApiRedux &&
    'reducer' in pdpApiRedux &&
    'middleware' in pdpApiRedux &&
    'reducerPath' in pdpCustomizationApiRedux &&
    'reducer' in pdpCustomizationApiRedux &&
    'middleware' in pdpCustomizationApiRedux &&
    'reducerPath' in pdpDeliveryApiRedux &&
    'reducer' in pdpDeliveryApiRedux &&
    'middleware' in pdpDeliveryApiRedux &&
    'reducerPath' in pdpStoreApiRedux &&
    'reducer' in pdpStoreApiRedux &&
    'middleware' in pdpStoreApiRedux &&
    'reducerPath' in pincodeApiRedux &&
    'reducer' in pincodeApiRedux &&
    'middleware' in pincodeApiRedux &&
    'reducerPath' in similarDesignsApiRedux &&
    'reducer' in similarDesignsApiRedux &&
    'middleware' in similarDesignsApiRedux &&
    'reducerPath' in matchingSetApiRedux &&
    'reducer' in matchingSetApiRedux &&
    'middleware' in matchingSetApiRedux &&
    'reducerPath' in posCartApiRedux &&
    'reducer' in posCartApiRedux &&
    'middleware' in posCartApiRedux &&
    'reducerPath' in sizerApiRedux &&
    'reducer' in sizerApiRedux &&
    'middleware' in sizerApiRedux &&
    'reducerPath' in featureFlagsApiRedux &&
    'reducer' in featureFlagsApiRedux &&
    'middleware' in featureFlagsApiRedux &&
    'middleware' in sizerApiRedux&&
    'reducerPath' in paymentApiRedux &&
    'reducer' in paymentApiRedux &&
    'middleware' in paymentApiRedux
    ;

  const reducer: Record<string, Reducer> = {};
  const middleware: Middleware[] = [];

  // Always include app reducers (non-API slices)
  reducer.abTesting = abTestingReducer as unknown as Reducer;
  reducer[PLP_INITIAL_SLICE_NAME] = plpInitialReducer as unknown as Reducer;
  reducer[PDP_INITIAL_SLICE_NAME] = pdpInitialReducer as unknown as Reducer;
  reducer[STORE_GIFT_CARD_INITIAL_SLICE_NAME] = storeGiftCardInitialReducer as unknown as Reducer;
  reducer[STORE_LOCATOR_INITIAL_SLICE_NAME] = storeLocatorInitialReducer as unknown as Reducer;
  reducer.loginUi = loginUiReducer as unknown as Reducer;
  reducer.searchUi = searchUiReducer as unknown as Reducer;
  reducer[VIDEO_CALL_SLICE_NAME] = videoCallReducer as unknown as Reducer;
  reducer.headerConfig = headerConfigReducer as unknown as Reducer;
  reducer.footerVisibility = footerVisibilityReducer as unknown as Reducer;
  reducer.newHomeUi = newHomeUiReducer as unknown as Reducer;
  reducer.appEnvironment = appEnvironmentReducer as unknown as Reducer;
  reducer[PINCODE_SLICE_NAME] = pincodeReducer as unknown as Reducer;
  reducer[MULTIVARIATE_EXPERIMENT_SLICE_NAME] = multivariateExperimentReducer as unknown as Reducer;

  if (hasReduxSupport) {
    // RTK Query adapter - add reducers and middleware
    const menuRedux = menuApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const searchRedux = searchApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const homePageRedux = homePageApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const userContextRedux = userContextApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const plpRedux = plpApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const bannerWidgetRedux = bannerWidgetApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const physicalStoreRedux = physicalStoreApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const wishlistRedux = wishlistApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const browsingHistoryRedux = browsingHistoryApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const videoCallRedux = videoCallApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const bsUtilRedux = bsUtilApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const posApprovalRedux = posApprovalApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const pdpRedux = pdpApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const pdpCustomizationRedux = pdpCustomizationApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const pdpDeliveryRedux = pdpDeliveryApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const pdpStoreRedux = pdpStoreApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const pincodeRedux = pincodeApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const similarDesignsRedux = similarDesignsApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const matchingSetRedux = matchingSetApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const posCartRedux = posCartApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const sizerRedux = sizerApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const featureFlagsRedux = featureFlagsApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };
    const paymentRedux = paymentApiRedux as { reducerPath: string; reducer: Reducer; middleware: Middleware };

    reducer[menuRedux.reducerPath] = menuRedux.reducer;
    reducer[searchRedux.reducerPath] = searchRedux.reducer;
    reducer[homePageRedux.reducerPath] = homePageRedux.reducer;
    reducer[userContextRedux.reducerPath] = userContextRedux.reducer;
    reducer[plpRedux.reducerPath] = plpRedux.reducer;
    reducer[bannerWidgetRedux.reducerPath] = bannerWidgetRedux.reducer;
    reducer[physicalStoreRedux.reducerPath] = physicalStoreRedux.reducer;
    reducer[wishlistRedux.reducerPath] = wishlistRedux.reducer;
    reducer[browsingHistoryRedux.reducerPath] = browsingHistoryRedux.reducer;
    reducer[videoCallRedux.reducerPath] = videoCallRedux.reducer;
    reducer[bsUtilRedux.reducerPath] = bsUtilRedux.reducer;
    reducer[posApprovalRedux.reducerPath] = posApprovalRedux.reducer;
    reducer[pdpRedux.reducerPath] = pdpRedux.reducer;
    reducer[pdpCustomizationRedux.reducerPath] = pdpCustomizationRedux.reducer;
    reducer[pdpDeliveryRedux.reducerPath] = pdpDeliveryRedux.reducer;
    reducer[pdpStoreRedux.reducerPath] = pdpStoreRedux.reducer;
    reducer[pincodeRedux.reducerPath] = pincodeRedux.reducer;
    reducer[similarDesignsRedux.reducerPath] = similarDesignsRedux.reducer;
    reducer[matchingSetRedux.reducerPath] = matchingSetRedux.reducer;
    reducer[posCartRedux.reducerPath] = posCartRedux.reducer;
    reducer[sizerRedux.reducerPath] = sizerRedux.reducer;
    reducer[featureFlagsRedux.reducerPath] = featureFlagsRedux.reducer;
    reducer[paymentRedux.reducerPath] = paymentRedux.reducer;
    middleware.push(
      menuRedux.middleware,
      searchRedux.middleware,
      homePageRedux.middleware,
      userContextRedux.middleware,
      plpRedux.middleware,
      bannerWidgetRedux.middleware,
      physicalStoreRedux.middleware,
      wishlistRedux.middleware,
      browsingHistoryRedux.middleware,
      videoCallRedux.middleware,
      bsUtilRedux.middleware,
      posApprovalRedux.middleware,
      pdpRedux.middleware,
      pdpCustomizationRedux.middleware,
      pdpDeliveryRedux.middleware,
      pdpStoreRedux.middleware,
      pincodeRedux.middleware,
      similarDesignsRedux.middleware,
      matchingSetRedux.middleware,
      sizerRedux.middleware,
      featureFlagsRedux.middleware,
      paymentRedux.middleware,
      posCartRedux.middleware
    );
  }
  // For non-Redux adapters, reducer and middleware arrays remain empty
  // Store is still created for other application state

  return configureStore({
    reducer,
    middleware: (getDefaultMiddleware) => {
      const defaultMiddleware = getDefaultMiddleware({
        serializableCheck: {
          // RTK Query stores non-serializable values in action metadata:
          // - meta.arg: Query arguments (may contain complex objects)
          // - meta.baseQueryMeta: Contains AbortController, function references
          // - payload.error: Error objects from baseQuery
          // This is expected behavior - RTK Query handles serialization internally
          ignoredActionPaths: [
            'meta.arg',
            'meta.baseQueryMeta',
            'payload.error',
          ],
        },
      });
      
      if (middleware.length > 0) {
        return defaultMiddleware.concat(...middleware);
      }
      return defaultMiddleware;
    },
    ...(preloadedState && { preloadedState }),
  });
}

// Create a store instance for type inference only (not exported)
// Use a try-catch to handle cases where Redux properties don't exist (non-Redux adapters)
let _storeForTypes: ReturnType<typeof createStore>;
try {
  _storeForTypes = createStore();
} catch {
  // If store creation fails (e.g., non-Redux adapter), create minimal store
  _storeForTypes = configureStore({ reducer: {} });
}

export type RootState = ReturnType<typeof _storeForTypes.getState>;
export type AppDispatch = typeof _storeForTypes.dispatch;

/**
 * Typed Redux Hooks
 * Use these instead of plain useDispatch and useSelector for type safety
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore = () => useStore<RootState>();
